/**
 * CrossZoneCelebration - "The Moment"
 * 
 * When a project is forked across creative disciplines, THIS is what
 * makes users screenshot it and share. A brief, beautiful animation
 * that shows two zones connecting with a particle trail and a single
 * sentence explaining the creative leap.
 * 
 * Triggered by: fork creation where parent zone !== child zone
 * Duration: ~4 seconds, then auto-dismisses
 * 
 * Place at: src/components/map/CrossZoneCelebration.jsx
 */

import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, X } from 'lucide-react';
import { ZONES } from '../../config/zones';

const CrossZoneCelebration = ({ 
  sourceZone, 
  targetZone, 
  sourceName, 
  targetName, 
  onDismiss 
}) => {
  const [phase, setPhase] = useState('enter'); // enter -> hold -> exit
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const particlesRef = useRef([]);
  
  const src = ZONES[sourceZone] || ZONES.smart;
  const tgt = ZONES[targetZone] || ZONES.smart;

  // Auto-dismiss after 4.5s
  useEffect(() => {
    const holdTimer = setTimeout(() => setPhase('hold'), 400);
    const exitTimer = setTimeout(() => setPhase('exit'), 3800);
    const dismissTimer = setTimeout(() => onDismiss?.(), 4500);
    return () => {
      clearTimeout(holdTimer);
      clearTimeout(exitTimer);
      clearTimeout(dismissTimer);
    };
  }, [onDismiss]);

  // Particle animation on the canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();

    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;

    // Create particles that flow from source color to target color
    const createParticle = () => ({
      x: w * 0.2 + Math.random() * w * 0.1,
      y: h * 0.5 + (Math.random() - 0.5) * 30,
      vx: 1.5 + Math.random() * 2.5,
      vy: (Math.random() - 0.5) * 0.8,
      size: 1.5 + Math.random() * 2.5,
      life: 1,
      decay: 0.008 + Math.random() * 0.006,
    });

    // Seed initial particles
    for (let i = 0; i < 20; i++) {
      const p = createParticle();
      p.x = w * 0.2 + Math.random() * w * 0.6;
      particlesRef.current.push(p);
    }

    let frame = 0;
    const animate = () => {
      ctx.clearRect(0, 0, w, h);
      frame++;

      // Spawn new particles
      if (frame % 3 === 0 && particlesRef.current.length < 60) {
        particlesRef.current.push(createParticle());
      }

      particlesRef.current.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += (Math.random() - 0.5) * 0.1;
        p.life -= p.decay;

        if (p.life <= 0) return;

        // Interpolate color from source to target based on x position
        const t = Math.min(1, Math.max(0, (p.x - w * 0.2) / (w * 0.6)));
        const alpha = p.life * 0.7;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        
        // Simple color blend using the zone colors
        ctx.fillStyle = t < 0.5 
          ? `${src.color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`
          : `${tgt.color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
        ctx.fill();

        // Glow
        if (p.size > 2) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
          const glowColor = t < 0.5 ? src.color : tgt.color;
          ctx.fillStyle = `${glowColor}${Math.round(alpha * 40).toString(16).padStart(2, '0')}`;
          ctx.fill();
        }
      });

      // Remove dead particles
      particlesRef.current = particlesRef.current.filter(p => p.life > 0);

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [src.color, tgt.color]);

  return (
    <div 
      className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center"
      style={{
        opacity: phase === 'enter' ? 0 : phase === 'exit' ? 0 : 1,
        transition: 'opacity 0.4s ease-out',
      }}
    >
      {/* Subtle backdrop pulse */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at center, ${src.color}08 0%, transparent 50%, ${tgt.color}08 100%)`,
        }}
      />

      {/* Main card */}
      <div 
        className="relative pointer-events-auto max-w-sm w-full mx-4"
        style={{
          transform: phase === 'enter' ? 'scale(0.9) translateY(10px)' : phase === 'exit' ? 'scale(0.95) translateY(-5px)' : 'scale(1) translateY(0)',
          transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Particle canvas behind the card */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full rounded-2xl"
          style={{ pointerEvents: 'none' }}
        />

        <div className="relative bg-zinc-950/90 backdrop-blur-2xl border border-zinc-800/50 rounded-2xl overflow-hidden">
          {/* Gradient top accent */}
          <div 
            className="h-[3px]" 
            style={{ 
              background: `linear-gradient(90deg, ${src.color}, ${tgt.color})` 
            }} 
          />
          
          <div className="p-5">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${src.color}15` }}>
                  <Sparkles className="w-3.5 h-3.5" style={{ color: src.color }} />
                </div>
                <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">
                  Creative Leap
                </span>
              </div>
              <button 
                onClick={onDismiss} 
                className="p-1 text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Zone connection visual */}
            <div className="flex items-center gap-3 mb-4">
              {/* Source zone */}
              <div className="flex-1 text-center">
                <div 
                  className="w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-1.5"
                  style={{ backgroundColor: `${src.color}15`, border: `1px solid ${src.color}30` }}
                >
                  <span className="text-lg">{src.emoji || '✨'}</span>
                </div>
                <p className="text-[11px] font-semibold" style={{ color: src.color }}>
                  {src.shortLabel}
                </p>
              </div>

              {/* Arrow trail */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {[0, 1, 2].map(i => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: i === 0 ? src.color : i === 2 ? tgt.color : `${src.color}80`,
                      opacity: 0.4 + (i * 0.3),
                      animation: `pulse 1.5s ease-in-out ${i * 0.2}s infinite`,
                    }}
                  />
                ))}
                <svg className="w-4 h-4 text-zinc-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
                {[0, 1, 2].map(i => (
                  <div
                    key={i + 3}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      backgroundColor: i === 2 ? tgt.color : `${tgt.color}80`,
                      opacity: 0.4 + (i * 0.3),
                      animation: `pulse 1.5s ease-in-out ${(i + 3) * 0.2}s infinite`,
                    }}
                  />
                ))}
              </div>

              {/* Target zone */}
              <div className="flex-1 text-center">
                <div 
                  className="w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-1.5"
                  style={{ backgroundColor: `${tgt.color}15`, border: `1px solid ${tgt.color}30` }}
                >
                  <span className="text-lg">{tgt.emoji || '✨'}</span>
                </div>
                <p className="text-[11px] font-semibold" style={{ color: tgt.color }}>
                  {tgt.shortLabel}
                </p>
              </div>
            </div>

            {/* The sentence that makes people screenshot */}
            <p className="text-center text-[13px] text-zinc-300 leading-relaxed">
              <span className="font-semibold" style={{ color: src.color }}>{sourceName || 'A project'}</span>
              {' '}just inspired{' '}
              <span className="font-semibold" style={{ color: tgt.color }}>{targetName || 'something new'}</span>
              {' '}across disciplines
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossZoneCelebration;